import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * Wings + compact card. When `flyRight`, exits horizontally off-screen.
 * Pass `flyMotion={false}` when a parent layer owns the horizontal exit.
 */
export function AngelWingsCard({
  ascending,
  flyRight = false,
  flyMotion = true,
  quietWings = false,
  children,
  className = '',
  style,
}) {
  const reduceMotion = useReducedMotion();

  const flyRightMotion = {
    x: reduceMotion ? 220 : 560,
    y: 0,
    opacity: 0,
    rotate: 0,
  };

  const flyUpMotion = {
    y: reduceMotion ? -48 : -100,
    opacity: 0,
    scale: reduceMotion ? 0.92 : 0.78,
    rotate: reduceMotion ? 0 : -4,
  };

  const wings = (
    <>
      <div className="mkt-angel-wings" aria-hidden>
        <motion.span
          className="mkt-angel-wing mkt-angel-wing--left"
          animate={
            ascending && !reduceMotion && !quietWings
              ? { rotate: [-10, -26, -14, -30, -16], scaleY: [1, 1.1, 1, 1.08, 1] }
              : { rotate: -12, scaleY: 1 }
          }
          transition={
            ascending && !reduceMotion && !quietWings
              ? { duration: 1.05, ease: 'easeInOut' }
              : { duration: 0.2 }
          }
        />
        <motion.span
          className="mkt-angel-wing mkt-angel-wing--right"
          animate={
            ascending && !reduceMotion && !quietWings
              ? { rotate: [10, 26, 14, 30, 16], scaleY: [1, 1.1, 1, 1.08, 1] }
              : { rotate: 12, scaleY: 1 }
          }
          transition={
            ascending && !reduceMotion && !quietWings
              ? { duration: 1.05, ease: 'easeInOut' }
              : { duration: 0.2 }
          }
        />
        {ascending && !reduceMotion && !quietWings ? (
          <>
            <motion.span
              className="mkt-angel-sparkle mkt-angel-sparkle--1"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: [0, 1, 0], scale: [0.5, 1, 0.3], x: [0, 24], y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
            />
            <motion.span
              className="mkt-angel-sparkle mkt-angel-sparkle--2"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: [0, 1, 0], scale: [0.5, 1, 0.3], x: [0, 32], y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
            />
          </>
        ) : null}
      </div>
      {children}
    </>
  );

  if (!flyMotion) {
    return (
      <div className={`mkt-angel-wings-card ${className}`.trim()} style={style}>
        {wings}
      </div>
    );
  }

  return (
    <motion.div
      className={`mkt-angel-wings-card ${className}`.trim()}
      style={style}
      initial={flyRight && ascending ? { x: 0, y: 0, opacity: 1, rotate: 0 } : false}
      animate={
        ascending
          ? flyRight
            ? flyRightMotion
            : flyUpMotion
          : { x: 0, y: 0, opacity: 1, scale: 1, rotate: 0 }
      }
      transition={
        ascending
          ? {
              duration: reduceMotion ? 0.75 : 1.15,
              ease: flyRight ? [0.4, 0, 0.2, 1] : [0.22, 0.68, 0.2, 1],
            }
          : { type: 'spring', stiffness: 480, damping: 30 }
      }
    >
      {wings}
    </motion.div>
  );
}
